import { PlayerAvatar } from "../../components/player-avatar";
import type { DisplayProjection } from "@saboteur/contracts";
import type { DisplayConnectionState } from "../../realtime/display-socket";
import { GameBoard } from "../board/game-board";
import { PlayerStrip } from "../board/player-strip";
import { QrPresentation } from "../lobby/display-link";
import "./display.css";
export interface DisplayScreenProps {
  readonly projection: DisplayProjection;
  readonly connectionState?: DisplayConnectionState;
}
export function DisplayScreen({
  projection: p,
  connectionState = "connected",
}: DisplayScreenProps) {
  const active = p.participants.find((player) => player.participantId === p.activeParticipantId);
  if (["LOBBY", "BETWEEN_ROUNDS", "COMPLETED"].includes(p.phase))
    return (
      <main className="display-lobby" data-audience="display">
        <header>
          <span className="wordmark">Saboteur</span>
        </header>
        <div className="display-lobby__content">
          <section>
            <h1 data-testid="active-turn">
              {p.phase === "COMPLETED" ? (
                "Game complete."
              ) : p.phase === "BETWEEN_ROUNDS" ? (
                `Round ${p.roundNumber} complete.`
              ) : (
                <>
                  Good friends.
                  <br />
                  Bad intentions.
                </>
              )}
            </h1>
            <p>
              {p.phase === "LOBBY"
                ? "Scan the code on your phone to join the mine."
                : p.phase === "COMPLETED"
                  ? "Gold shared. The host can start a new game."
                  : `Gold shared. Waiting for the host to start round ${p.roundNumber + 1}.`}
            </p>
            <div className="display-invite">
              {p.joinCode ? (
                <QrPresentation
                  value={
                    p.joinUrl ?? new URL(`/room/${p.joinCode}`, window.location.origin).toString()
                  }
                  label="Scan to join this room"
                />
              ) : null}
              <div>
                <p>ROOM CODE</p>
                <strong>{p.joinCode ?? "Ask the host for an invite"}</strong>
                <p>Waiting for the host to start.</p>
              </div>
            </div>
          </section>
          <section className="display-crew">
            <h2>
              The crew <span>{p.participants.length}/10</span>
            </h2>
            {p.participants.map((player) => (
              <div key={player.participantId}>
                <PlayerAvatar seat={player.avatarIndex ?? player.seatIndex} />
                <strong>
                  {player.displayName}
                  {player.isHost ? " · Host" : ""}
                </strong>
                <span>
                  {p.phase === "COMPLETED"
                    ? `${player.finalGoldTotal ?? 0} gold${p.finalResults?.find((result) => result.participantId === player.participantId)?.winner ? " · Winner" : ""}`
                    : !player.connected
                      ? "Offline"
                      : p.phase === "BETWEEN_ROUNDS"
                        ? "Ready"
                        : player.ready
                          ? "✓ Ready"
                          : "Getting ready"}
                </span>
              </div>
            ))}
          </section>
        </div>
        {connectionState !== "connected" ? <p role="status">Reconnecting to the room…</p> : null}
      </main>
    );
  if (p.phase === "ABANDONED")
    return (
      <main className="centered-page" data-audience="display">
        <section className="surface stack">
          <p className="eyebrow">Read-only display</p>
          <h1 data-testid="active-turn">Session ended</h1>
          <p>Ask the host for a new game invite.</p>
        </section>
      </main>
    );
  return (
    <main className="display-screen" data-audience="display">
      <header className="display-header">
        <span className="wordmark">Saboteur</span>
        <span data-testid="round-indicator">Round {p.roundNumber} of 3</span>
        <h1 data-testid="active-turn">
          {p.phase === "PLAYING"
            ? `${active?.displayName ?? "Player"}’s turn`
            : p.phase === "GOLD_SELECTION"
              ? "Choosing gold rewards"
              : "Round complete"}
        </h1>
      </header>
      <PlayerStrip participants={p.participants} activeParticipantId={p.activeParticipantId} />
      <GameBoard
        board={p.board}
        participants={p.participants}
        activeParticipantId={p.activeParticipantId}
        recentActions={p.recentActions}
        readOnly
      />
      {p.roundOutcome ? (
        <aside className="display-round-result">
          <h2>
            {p.roundOutcome.winningTeam === "MINERS"
              ? "Gold found!"
              : p.roundOutcome.winningTeam === "SABOTEURS"
                ? "Saboteurs win!"
                : "No gold this time"}
          </h2>
          {p.goldSharing ? (
            <section className="display-gold-sharing" aria-label="Gold sharing">
              <p>
                <strong>{p.goldSharing.totalGold} gold</strong> to share
              </p>
              <progress
                value={p.goldSharing.totalCards - p.goldSharing.remainingCards}
                max={Math.max(1, p.goldSharing.totalCards)}
                aria-label="Gold cards shared"
              />
              <p role="status">
                {p.goldSharing.totalCards - p.goldSharing.remainingCards} of{" "}
                {p.goldSharing.totalCards} cards shared · {p.goldSharing.remainingGold} gold
                remaining
                <br />
                {p.participants.find(
                  (player) => player.participantId === p.goldSharing?.choosingParticipantId,
                )?.displayName ?? "A miner"}{" "}
                is choosing
              </p>
              <ul>
                {p.goldSharing.allocations.map((allocation) => (
                  <li
                    key={allocation.participantId}
                    aria-current={
                      allocation.participantId === p.goldSharing?.choosingParticipantId
                        ? "true"
                        : undefined
                    }
                  >
                    <span>
                      {
                        p.participants.find(
                          (player) => player.participantId === allocation.participantId,
                        )?.displayName
                      }
                    </span>
                    <strong>{allocation.gold} gold</strong>
                    <small>
                      {allocation.participantId === p.goldSharing?.choosingParticipantId
                        ? "Choosing…"
                        : `${allocation.cardCount} cards`}
                    </small>
                  </li>
                ))}
              </ul>
            </section>
          ) : (
            <p>Sharing the gold…</p>
          )}
          <div className="round-crew">
            {p.participants.map((player) => (
              <div key={player.participantId}>
                <PlayerAvatar seat={player.avatarIndex ?? player.seatIndex} />
                <strong>{player.displayName}</strong>
                <small>{player.revealedRole?.toLowerCase()}</small>
              </div>
            ))}
          </div>
        </aside>
      ) : null}
      <footer>
        <span>
          {p.drawPileCount} draw · {p.discardCount} discarded
        </span>
        {connectionState !== "connected" ? <strong role="status">Reconnecting…</strong> : null}
      </footer>
    </main>
  );
}
